import { CartItems } from '@/hooks/CartContext';
import { HandlePrices } from '@/shared/calculatePrices';
import { normalizePrecoListForCalc } from '@/shared';
import { ProductProps } from '@/types';
import {
    buildComboInstanceAllocationsMap,
    getComboInstanceStatuses,
    getGroupAllocatedQuantityForInstance,
    getOtherInstancesAllocatedQuantity,
    parseComboInstanceId,
    formatComboInstanceGroupsProgressLabel,
    type ComboInstanceGroupStatus,
} from './comboCartInstances';
import { mergeCompleteComboCartSections, type CartDisplaySection } from './comboCartDisplayMerge';

export { formatComboInstanceGroupsProgressLabel } from './comboCartInstances';
export {
    getProductEmbalagemQuantity,
    getProductEmbalagensMatchingComboSku,
    hasProductComboEligiblePackaging,
    resolveComboEligibleProduct,
} from './comboEligiblePackaging';
export type { ComboEligibleSkuRef } from './comboEligiblePackaging';

export interface ComboEligibleSku {
    cod_produto: number;
    qtd_embalagem: number;
}

export interface ComboRequirementGroup {
    id?: string;
    id_grupo?: string;
    desc_grupo: string;
    quantidade_base_por_kit: number;
    skus_elegiveis: ComboEligibleSku[];
}

export interface ComboGroupProgress {
    required: number;
    cartQuantity: number;
    fulfilled: number;
    remaining: number;
    progressPercent: number;
}

export interface ComboProgress extends ComboGroupProgress {
    kitTotal: number;
    groupProgress: Array<ComboGroupProgress & { grupo: ComboRequirementGroup }>;
}

export interface ComboCartItemAllocation {
    id_campanha: string;
    nome_campanha: string;
    grupoId: string;
    /** Identidade estável da instância (kit) do combo no carrinho. */
    comboInstanceId?: string;
    /** Índice 1-based da instância dentro do combo. */
    kitIndex?: number;
    /** Identidade estável da relação cartItem + combo + instância + grupo + SKU. */
    allocationId?: string;
    allocatedPackaging: number;
    cod_produto: number;
    qtd_embalagem: number;
    /** Quando false, a embalagem conta para o grupo visual mas não recebe desconto (ex.: combo em montagem). */
    appliesDiscount?: boolean;
}

export type ComboCartItemsMap = Record<string, ComboCartItemAllocation[]>;

export type ComboCartDisplayPortion = 'combo' | 'excess';

export interface ComboCartDisplaySplit<T extends { uid: string; quantity: number }> {
    item: T;
    portion?: ComboCartDisplayPortion;
    displayQuantity: number;
    siblingQuantity: number;
    displayKey: string;
}

export const getCartItemPackagingQty = (item: Pick<CartItems, 'option_uid'>) => {
    const [, qtd] = item.option_uid?.split('_') || [];
    return Number(qtd);
};

/** Quantidade desta linha alocada ao combo com direito a desconto (kits completos dentro do max_combo). */
export const getComboDiscountParticipatingQuantityForCartItem = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid' | 'quantity'>,
    allocations: ComboCartItemAllocation[] | undefined
): number => {
    if (!allocations?.length || item.quantity <= 0) return 0;

    const qtdEmbalagem = getCartItemPackagingQty(item);

    const allocatedForLine = allocations
        .filter(
            (entry) =>
                Number(entry.cod_produto) === Number(item.sku) &&
                Number(entry.qtd_embalagem) === qtdEmbalagem &&
                entry.appliesDiscount !== false
        )
        .reduce((sum, entry) => sum + (entry.allocatedPackaging || 0), 0);

    return Math.min(item.quantity, allocatedForLine);
};

/** Quantidade desta linha (produto + embalagem) que conta para o combo no grupo.
 *  A cota é do grupo (mix de produtos); o excedente é calculado só nesta linha. */
export const getComboParticipatingQuantityForCartItem = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid' | 'quantity'>,
    allocations: ComboCartItemAllocation[] | undefined
): number => {
    if (!allocations?.length || item.quantity <= 0) return 0;

    const qtdEmbalagem = getCartItemPackagingQty(item);

    const allocatedForLine = allocations
        .filter(
            (entry) =>
                Number(entry.cod_produto) === Number(item.sku) &&
                Number(entry.qtd_embalagem) === qtdEmbalagem
        )
        .reduce((sum, entry) => sum + (entry.allocatedPackaging || 0), 0);

    return Math.min(item.quantity, allocatedForLine);
};

/** @deprecated Use getComboParticipatingQuantityForCartItem */
export const getComboParticipatingQuantity = (
    allocations: ComboCartItemAllocation[] | undefined,
    totalQuantity: number
): number => {
    if (!allocations?.length || totalQuantity <= 0) return 0;

    const allocatedForLine = allocations.reduce(
        (sum, entry) => sum + (entry.allocatedPackaging || 0),
        0
    );

    return Math.min(totalQuantity, allocatedForLine);
};

export const splitCartLineForComboDisplay = <T extends { uid: string; quantity: number }>(
    item: T,
    participatingQuantity: number,
    allowExcessSplit = false
): ComboCartDisplaySplit<T>[] => {
    const participating = Math.max(participatingQuantity, 0);

    if (!allowExcessSplit) {
        return [
            {
                item,
                ...(participating > 0 ? { portion: 'combo' as const } : {}),
                displayQuantity: item.quantity,
                siblingQuantity: 0,
                displayKey: item.uid,
            },
        ];
    }

    if (participating <= 0) {
        return [
            {
                item,
                displayQuantity: item.quantity,
                siblingQuantity: 0,
                displayKey: item.uid,
            },
        ];
    }

    const comboQty = Math.min(item.quantity, participating);
    const excessQty = Math.max(item.quantity - comboQty, 0);

    if (comboQty > 0 && excessQty > 0) {
        return [
            {
                item: { ...item, quantity: comboQty },
                portion: 'combo',
                displayQuantity: comboQty,
                siblingQuantity: excessQty,
                displayKey: `${item.uid}-combo`,
            },
            {
                item: { ...item, quantity: excessQty },
                portion: 'excess',
                displayQuantity: excessQty,
                siblingQuantity: comboQty,
                displayKey: `${item.uid}-excess`,
            },
        ];
    }

    return [
        {
            item,
            portion: 'combo',
            displayQuantity: item.quantity,
            siblingQuantity: 0,
            displayKey: item.uid,
        },
    ];
};

export type {
    CartComboDisplaySection,
    CartDisplaySection,
    CartStandaloneDisplaySection,
} from './comboCartDisplayMerge';
export { aggregateMergedComboDisplayItems, mergeCompleteComboCartSections } from './comboCartDisplayMerge';
export { getLiveComboDisplayPortionQuantity } from './comboCartDisplayMerge';

export interface CartDisplayItemForGrouping {
    uid: string;
    displayKey?: string;
    comboPortion?: ComboCartDisplayPortion;
    sku?: number;
    option_uid?: string;
    displayQuantity?: number;
    cartQuantity?: number;
    quantity?: number;
    comboDiscountQuantity?: number;
    showLooseComboParticipant?: boolean;
    comboSourceUids?: string[];
    comboInstanceId?: string;
    kitIndex?: number;
    allocationId?: string;
    grupoId?: string;
    idCampanha?: string;
}

const getComboDisplayItemKey = (item: CartDisplayItemForGrouping) => {
    const portion = item.comboPortion ?? 'combo';
    const instance = item.comboInstanceId ?? 'none';
    const allocation = item.allocationId ?? 'none';
    return `${item.sku ?? ''}-${item.option_uid ?? ''}-${portion}-${instance}-${allocation}`;
};

export const getPrimaryComboInstanceForCartDisplay = (
    item: Pick<CartDisplayItemForGrouping, 'comboPortion' | 'comboInstanceId'>,
    allocations: ComboCartItemAllocation[] | undefined
): ComboCartItemAllocation | null => {
    if (item.comboPortion === 'excess' || !allocations?.length) return null;

    const relevant = allocations.filter((entry) => entry.allocatedPackaging > 0);

    if (item.comboInstanceId) {
        const forInstance = relevant.filter((entry) => entry.comboInstanceId === item.comboInstanceId);
        if (forInstance.length) {
            return forInstance.find((entry) => entry.appliesDiscount !== false) || forInstance[0];
        }
    }

    const unique = new Map<string, ComboCartItemAllocation>();
    relevant.forEach((entry) => {
        const key = entry.comboInstanceId || entry.id_campanha;
        if (!unique.has(key)) {
            unique.set(key, entry);
        }
    });

    return unique.values().next().value ?? null;
};

export const getPrimaryComboIdForCartDisplay = (
    item: Pick<CartDisplayItemForGrouping, 'comboPortion' | 'comboInstanceId'>,
    allocations: ComboCartItemAllocation[] | undefined
): ComboCartItemAllocation | null => getPrimaryComboInstanceForCartDisplay(item, allocations);

/** Item fora da seção agrupada que ainda pertence a um combo (excedente da linha). */
export const isStandaloneComboDisplayItem = (
    item: Pick<CartDisplayItemForGrouping, 'comboPortion'>,
    allocations: ComboCartItemAllocation[] | undefined
): boolean => {
    if (!allocations?.length) return false;
    if (item.comboPortion === 'excess') return true;

    return getPrimaryComboIdForCartDisplay(item, allocations) === null;
};

/** @deprecated Use isStandaloneComboDisplayItem */
export const isLooseComboParticipantItem = isStandaloneComboDisplayItem;

/** Agrupa itens de exibição do carrinho por instância do combo (kit), preservando a ordem geral. */
export const buildCartDisplaySections = <T extends CartDisplayItemForGrouping>(
    displayItems: T[],
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined,
    getInstanceStatuses?: (
        idCampanha: string
    ) => Array<{
        comboInstanceId: string;
        kitIndex: number;
        isComplete: boolean;
        appliesDiscount: boolean;
        groups: ComboInstanceGroupStatus[];
    }> | undefined
): CartDisplaySection<T>[] => {
    const instanceGroups = new Map<
        string,
        {
            id_campanha: string;
            comboInstanceId: string;
            kitIndex: number;
            nome_campanha: string;
            items: T[];
            order: number;
        }
    >();
    const standalone: Array<{ item: T; order: number }> = [];

    displayItems.forEach((item, index) => {
        const primaryInstance = getPrimaryComboInstanceForCartDisplay(item, getAllocations(item.uid));

        if (!primaryInstance?.comboInstanceId) {
            standalone.push({ item, order: index });
            return;
        }

        const groupKey = primaryInstance.comboInstanceId;
        const existing = instanceGroups.get(groupKey);

        if (existing) {
            existing.items.push(item);
            return;
        }

        instanceGroups.set(groupKey, {
            id_campanha: primaryInstance.id_campanha,
            comboInstanceId: primaryInstance.comboInstanceId,
            kitIndex: primaryInstance.kitIndex ?? 1,
            nome_campanha: primaryInstance.nome_campanha,
            items: [item],
            order: index,
        });
    });

    const looseComboIds = new Set<string>();
    standalone.forEach(({ item }) => {
        const allocations = getAllocations(item.uid);
        if (!isStandaloneComboDisplayItem(item, allocations)) return;

        allocations?.forEach((entry) => {
            looseComboIds.add(entry.id_campanha);
        });
    });

    const sections: CartDisplaySection<T>[] = [
        ...Array.from(instanceGroups.values()).map((group) => {
            const statuses = getInstanceStatuses?.(group.id_campanha) ?? [];
            const instanceStatus = statuses.find(
                (status) => status.comboInstanceId === group.comboInstanceId
            );
            const totalGroupsCount = instanceStatus?.groups.length ?? 0;
            const completeGroupsCount =
                instanceStatus?.groups.filter((entry) => entry.isComplete).length ?? 0;
            const isInstanceComplete = instanceStatus?.isComplete ?? false;

            return {
                type: 'combo' as const,
                id_campanha: group.id_campanha,
                comboInstanceId: group.comboInstanceId,
                kitIndex: group.kitIndex,
                nome_campanha: group.nome_campanha,
                items: aggregateComboDisplayItems(group.items),
                order: group.order,
                isInstanceComplete,
                appliesDiscount: instanceStatus?.appliesDiscount ?? false,
                hasLooseComboProducts: looseComboIds.has(group.id_campanha),
                completeGroupsCount,
                totalGroupsCount,
                groupsProgressLabel:
                    !isInstanceComplete && totalGroupsCount > 0
                        ? formatComboInstanceGroupsProgressLabel(completeGroupsCount, totalGroupsCount)
                        : '',
            };
        }),
        ...standalone.map(({ item, order }) => ({
            type: 'standalone' as const,
            items: [
                {
                    ...item,
                    showLooseComboParticipant: isStandaloneComboDisplayItem(item, getAllocations(item.uid)),
                },
            ] as [T],
            order,
        })),
    ];

    return mergeCompleteComboCartSections(sections.sort((a, b) => a.order - b.order));
};

/** Consolida linhas do mesmo produto+embalagem+instância dentro da seção do combo. */
export const aggregateComboDisplayItems = <T extends CartDisplayItemForGrouping>(items: T[]): T[] => {
    const buckets = new Map<string, T>();

    items.forEach((item) => {
        const key = getComboDisplayItemKey(item);
        const displayQty = item.displayQuantity ?? item.quantity ?? 0;
        const cartQty = item.cartQuantity ?? item.quantity ?? 0;
        const existing = buckets.get(key);

        if (!existing) {
            buckets.set(key, {
                ...item,
                displayQuantity: displayQty,
                cartQuantity: cartQty,
                quantity: cartQty,
                comboDiscountQuantity: item.comboDiscountQuantity ?? 0,
                displayKey: `combo-agg-${key}`,
                comboSourceUids: [item.uid],
            });
            return;
        }

        const sourceUids = existing.comboSourceUids ?? [existing.uid];
        const mergedDisplayQty = (existing.displayQuantity ?? 0) + displayQty;
        const lineCartQty = Math.max(existing.cartQuantity ?? 0, cartQty);

        buckets.set(key, {
            ...existing,
            displayQuantity: mergedDisplayQty,
            cartQuantity: lineCartQty,
            quantity: lineCartQty,
            comboDiscountQuantity: (existing.comboDiscountQuantity ?? 0) + (item.comboDiscountQuantity ?? 0),
            comboSourceUids: sourceUids.includes(item.uid) ? sourceUids : [...sourceUids, item.uid],
        });
    });

    return Array.from(buckets.values());
};

export interface ComboKitStatus {
    completeKitsInCart: number;
    maxComboLimit: number | null;
    activeCompleteKits: number;
    hasInProgressKit: boolean;
}

export const getCartItemPackagingCount = (item: CartItems) => item.quantity;

export const isCartItemMatchingEligibleSku = (
    item: Pick<CartItems, 'sku' | 'option_uid'>,
    sku: ComboEligibleSku
) => {
    if (Number(item.sku) !== Number(sku.cod_produto)) return false;

    const [, qtd] = item.option_uid?.split('_') || [];
    return Number(qtd) === Number(sku.qtd_embalagem);
};

export const normalizeComboName = (combo: { id_campanha?: string; nome_campanha?: string }) => {
    if (combo.nome_campanha) return combo.nome_campanha;

    return combo?.id_campanha?.split('__').join(' + ')?.split('_').join(' ') || combo.id_campanha || '';
};

export const getComboDiscountLabel = (combo: { regras_beneficio?: Array<{ tipo_desconto?: string; valor_percentual?: number | null }> }) => {
    const regra = combo?.regras_beneficio?.[0];

    if (!regra) return '-';

    if (regra.tipo_desconto === 'PERCENTUAL' && regra.valor_percentual != null) {
        return `${regra.valor_percentual}%`;
    }

    if (regra.tipo_desconto === 'PRECO_FIXO') {
        return 'Preço fixo';
    }

    return '-';
};

export interface ComboStripMessage {
    prefix: string;
    highlight: string;
    suffix: string;
}

export const getComboStripMessage = (
    combo: {
        id_campanha?: string;
        nome_campanha?: string;
        desc_campanha?: string;
        regras_beneficio?: Array<{ tipo_desconto?: string; valor_percentual?: number | null }>;
    },
    grupoLabel?: string,
    options?: { includeBenefit?: boolean }
): ComboStripMessage => {
    const name = normalizeComboName(combo);
    const title = grupoLabel ? `${name} — ${grupoLabel}` : name;

    if (options?.includeBenefit === false) {
        return {
            prefix: title,
            highlight: '',
            suffix: '',
        };
    }

    const regra = combo?.regras_beneficio?.[0];

    if (regra?.tipo_desconto === 'PERCENTUAL' && regra.valor_percentual != null) {
        return {
            prefix: `${title} - Obtenha até `,
            highlight: `${regra.valor_percentual}% de desconto`,
            suffix: ' em produtos selecionados',
        };
    }

    if (regra?.tipo_desconto === 'PRECO_FIXO') {
        return {
            prefix: `${title} - `,
            highlight: 'Preço fixo',
            suffix: ' em produtos selecionados',
        };
    }

    if (combo.desc_campanha) {
        return {
            prefix: `${title} - `,
            highlight: '',
            suffix: combo.desc_campanha,
        };
    }

    return {
        prefix: title,
        highlight: '',
        suffix: '',
    };
};

export interface ActiveComboInCart {
    combo: any;
    progress: ComboProgress;
}

export const getCombosByStore = (combos: unknown, codLoja?: number) => {
    if (!Array.isArray(combos) || !codLoja) return [];

    const combosByStore = combos.find(
        (combo: { cod_loja: string | number }) => Number(combo.cod_loja) === codLoja
    );

    return combosByStore?.combos || [];
};

export const getGroupCartQuantity = (grupo: ComboRequirementGroup, cartItems: CartItems[]) => {
    const eligibleSkus = grupo.skus_elegiveis || [];

    return cartItems.reduce((total, item) => {
        const matchesEligible = eligibleSkus.some((sku) => isCartItemMatchingEligibleSku(item, sku));
        if (!matchesEligible) return total;

        return total + getCartItemPackagingCount(item);
    }, 0);
};

export const buildGroupProgress = (grupo: ComboRequirementGroup, cartItems: CartItems[]): ComboGroupProgress => {
    const required = grupo.quantidade_base_por_kit ?? 0;
    const cartQuantity = getGroupCartQuantity(grupo, cartItems);
    const fulfilled = Math.min(cartQuantity, required);
    const remaining = Math.max(required - cartQuantity, 0);
    const progressPercent = required > 0 ? Math.min((fulfilled / required) * 100, 100) : 0;

    return {
        required,
        cartQuantity,
        fulfilled,
        remaining,
        progressPercent,
    };
};

export const getGrupoLabel = (grupo?: ComboRequirementGroup | null, fallback = 'Grupo'): string => {
    if (!grupo) return fallback;

    const raw = grupo as unknown as Record<string, unknown>;
    const candidates = [raw.desc_grupo, raw.Desc_grupo, raw.id_grupo, raw.id];

    for (const value of candidates) {
        if (value != null && String(value).trim()) {
            return String(value).trim();
        }
    }

    return fallback;
};

export const getGroupId = (grupo: ComboRequirementGroup): string => {
    const raw = grupo as unknown as Record<string, unknown>;
    const candidates = [raw.id, raw.id_grupo, raw.desc_grupo, raw.Desc_grupo];

    for (const value of candidates) {
        if (value != null && String(value).trim()) {
            return String(value).trim();
        }
    }

    return '';
};

/** Todos os identificadores possíveis de um grupo (id, id_grupo, desc_grupo…). */
export const getGrupoIdCandidates = (grupo: ComboRequirementGroup): string[] => {
    const raw = grupo as unknown as Record<string, unknown>;
    const ids = new Set<string>();

    [raw.id, raw.id_grupo, raw.desc_grupo, raw.Desc_grupo, getGroupId(grupo)].forEach((value) => {
        if (value != null && String(value).trim()) {
            ids.add(String(value).trim());
        }
    });

    return Array.from(ids);
};

export const allocationMatchesGrupo = (
    entry: ComboCartItemAllocation,
    grupo: ComboRequirementGroup
): boolean => {
    if (!entry.grupoId) return false;

    const canonicalId = getGroupId(grupo);
    if (canonicalId && entry.grupoId === canonicalId) return true;

    return getGrupoIdCandidates(grupo).includes(entry.grupoId);
};

export const findComboRequirementGroup = (
    groups: ComboRequirementGroup[] | undefined,
    grupoId: string | undefined
): ComboRequirementGroup | undefined => {
    if (!groups?.length || !grupoId) return undefined;

    return groups.find((grupo) => getGrupoIdCandidates(grupo).includes(grupoId));
};

/** Grupos de exigência do combo em que este produto (sku + embalagem) é elegível. */
export const findComboRequirementGroupsForCartItem = (
    combo: { grupos_exigencia?: ComboRequirementGroup[] } | undefined,
    item?: Pick<CartItems, 'sku' | 'option_uid'>
): ComboRequirementGroup[] => {
    if (!combo?.grupos_exigencia?.length || !item) return [];

    return combo.grupos_exigencia.filter((grupo) =>
        (grupo.skus_elegiveis || []).some((sku) => isCartItemMatchingEligibleSku(item, sku))
    );
};

/** Grupo de exigência ao qual este produto (sku + embalagem) pertence no combo. */
export const findPrimaryComboRequirementGroupForCartItem = (
    combo: { grupos_exigencia?: ComboRequirementGroup[] } | undefined,
    item?: Pick<CartItems, 'sku' | 'option_uid'>,
    lineAllocations?: ComboCartItemAllocation[]
): ComboRequirementGroup | undefined => {
    const matches = findComboRequirementGroupsForCartItem(combo, item);

    if (!matches.length) return undefined;
    if (matches.length === 1) return matches[0];

    const allocGrupoId = lineAllocations?.find((entry) => entry.grupoId)?.grupoId;
    if (allocGrupoId) {
        const fromAllocation = matches.find((grupo) => allocationMatchesGrupo({ grupoId: allocGrupoId } as ComboCartItemAllocation, grupo));
        if (fromAllocation) return fromAllocation;
    }

    return matches[0];
};

export const allocationBelongsToGrupo = (
    entry: ComboCartItemAllocation,
    grupo: ComboRequirementGroup
): boolean => {
    return (grupo.skus_elegiveis || []).some(
        (sku) =>
            Number(entry.cod_produto) === Number(sku.cod_produto) &&
            Number(entry.qtd_embalagem) === Number(sku.qtd_embalagem)
    );
};

/** Alocações desta linha para um combo e grupo específicos. */
export const getLineAllocationsForComboAndGrupo = (
    allocations: ComboCartItemAllocation[] | undefined,
    idCampanha: string,
    grupo: ComboRequirementGroup,
    item?: Pick<CartItems, 'sku' | 'option_uid'>
): ComboCartItemAllocation[] => {
    return getLineAllocationsForCombo(allocations, idCampanha, item).filter((entry) =>
        allocationBelongsToGrupo(entry, grupo)
    );
};

export interface ComboStripGrupoEntry {
    grupo: ComboRequirementGroup;
    grupoId: string;
    comboInstanceId: string;
    kitIndex: number;
    lineAllocations: ComboCartItemAllocation[];
}

/** Resolve grupos deste produto por instância do combo (produto → grupo → instância). */
export const resolveComboStripGruposForLine = (
    combo: { grupos_exigencia?: ComboRequirementGroup[] },
    allocations: ComboCartItemAllocation[] | undefined,
    idCampanha: string,
    item?: Pick<CartItems, 'sku' | 'option_uid'>,
    comboInstanceId?: string
): ComboStripGrupoEntry[] => {
    const lineComboAllocs = getLineAllocationsForCombo(allocations, idCampanha, item).filter(
        (entry) => !comboInstanceId || entry.comboInstanceId === comboInstanceId
    );

    if (!lineComboAllocs.length) return [];

    const productGrupo = findPrimaryComboRequirementGroupForCartItem(combo, item, lineComboAllocs);
    const instanceIds = Array.from(
        new Set(lineComboAllocs.map((entry) => entry.comboInstanceId).filter(Boolean) as string[])
    ).sort((a, b) => {
        const ka = parseComboInstanceId(a)?.kitIndex ?? 0;
        const kb = parseComboInstanceId(b)?.kitIndex ?? 0;
        return ka - kb;
    });

    return instanceIds.flatMap((instanceId) => {
        const instanceAllocs = lineComboAllocs.filter((entry) => entry.comboInstanceId === instanceId);
        const grupo =
            productGrupo ||
            findComboRequirementGroup(
                combo.grupos_exigencia,
                instanceAllocs.find((entry) => entry.grupoId)?.grupoId
            );

        if (!grupo) return [];

        const grupoId = getGroupId(grupo);
        const grupoLineAllocs = instanceAllocs.filter((entry) => allocationBelongsToGrupo(entry, grupo));

        if (!grupoLineAllocs.length) return [];

        return [
            {
                grupo,
                grupoId,
                comboInstanceId: instanceId,
                kitIndex: instanceAllocs[0]?.kitIndex ?? parseComboInstanceId(instanceId)?.kitIndex ?? 1,
                lineAllocations: grupoLineAllocs,
            },
        ];
    });
};

export interface CartItemComboStripStatus {
    isGroupComplete: boolean;
    remaining: number;
    grupoLabel: string;
    comboName: string;
}

/** Alocações desta linha (uid + sku + embalagem) para um combo específico. */
export const getLineAllocationsForCombo = (
    allocations: ComboCartItemAllocation[] | undefined,
    idCampanha: string,
    item?: Pick<CartItems, 'sku' | 'option_uid'>
): ComboCartItemAllocation[] => {
    if (!allocations?.length) return [];

    const qtdEmbalagem = item ? getCartItemPackagingQty(item) : null;

    return allocations.filter((entry) => {
        if (entry.id_campanha !== idCampanha || entry.allocatedPackaging <= 0) return false;
        if (!item) return true;
        if (Number(entry.cod_produto) !== Number(item.sku)) return false;
        if (qtdEmbalagem != null && Number(entry.qtd_embalagem) !== qtdEmbalagem) return false;
        return true;
    });
};

/** Combos distintos em que esta linha do carrinho participa. */
export const getComboIdsForCartLine = (
    allocations: ComboCartItemAllocation[] | undefined,
    item?: Pick<CartItems, 'sku' | 'option_uid'>
): string[] => {
    if (!allocations?.length) return [];

    const qtdEmbalagem = item ? getCartItemPackagingQty(item) : null;
    const ids = new Set<string>();

    allocations.forEach((entry) => {
        if (entry.allocatedPackaging <= 0) return;
        if (item) {
            if (Number(entry.cod_produto) !== Number(item.sku)) return;
            if (qtdEmbalagem != null && Number(entry.qtd_embalagem) !== qtdEmbalagem) return;
        }
        ids.add(entry.id_campanha);
    });

    return Array.from(ids);
};

/** Quantidade alocada ao combo/grupo (só linhas com alocação — evita interferência de produto duplicado avulso). */
export const getGroupComboAllocatedQuantity = (
    grupo: ComboRequirementGroup,
    idCampanha: string,
    cartItems: CartItems[],
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined,
    mode: 'all' | 'discount' | 'in_progress' = 'all'
): number => {
    return cartItems.reduce((total, item) => {
        const allocs = getAllocations(item.uid) || [];
        const lineTotal = allocs
            .filter(
                (entry) =>
                    entry.id_campanha === idCampanha &&
                    allocationBelongsToGrupo(entry, grupo) &&
                    entry.allocatedPackaging > 0
            )
            .filter((entry) => {
                if (mode === 'discount') return entry.appliesDiscount !== false;
                if (mode === 'in_progress') return entry.appliesDiscount === false;
                return true;
            })
            .reduce((sum, entry) => sum + entry.allocatedPackaging, 0);

        return total + lineTotal;
    }, 0);
};

export interface CartLineComboStripContext {
    comboPortion?: 'combo' | 'excess';
    displayQuantity?: number;
    comboDiscountQuantity?: number;
}

/** Uma alocação por combo para o selo da linha (evita duplicar faixa no mesmo produto). */
export const getStripAllocationsForCartItem = (
    allocations: ComboCartItemAllocation[] | undefined,
    item?: Pick<CartItems, 'sku' | 'option_uid'>
): ComboCartItemAllocation[] => {
    const comboIds = getComboIdsForCartLine(allocations, item);

    return comboIds.map((idCampanha) => {
        const lineAllocs = getLineAllocationsForCombo(allocations, idCampanha, item);
        return (
            lineAllocs.find((entry) => entry.appliesDiscount !== false) ||
            lineAllocs[0] || {
                id_campanha: idCampanha,
                nome_campanha: idCampanha,
                grupoId: '',
                allocatedPackaging: 0,
                cod_produto: item?.sku ?? 0,
                qtd_embalagem: 0,
            }
        );
    });
};

/** Embalagens elegíveis do grupo já consumidas por OUTROS combos (não conta de novo neste combo). */
export const getGroupQuantityConsumedByOtherCombos = (
    grupo: ComboRequirementGroup,
    idCampanha: string,
    cartItems: CartItems[],
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined
): number => {
    return cartItems.reduce((total, item) => {
        const allocs = getAllocations(item.uid) || [];
        const lineTotal = allocs
            .filter(
                (entry) =>
                    entry.id_campanha !== idCampanha &&
                    allocationBelongsToGrupo(entry, grupo) &&
                    entry.allocatedPackaging > 0
            )
            .reduce((sum, entry) => sum + entry.allocatedPackaging, 0);

        return total + lineTotal;
    }, 0);
};

/** Quantidade cumprida no grupo SÓ deste combo: prioriza alocação ao combo; senão usa os
 *  itens elegíveis ainda livres (descontando os já consumidos por outros combos). */
export const getGrupoFulfilledQuantity = (
    grupo: ComboRequirementGroup,
    idCampanha: string,
    cartItems: CartItems[],
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined
): number => {
    const allocated = getGroupComboAllocatedQuantity(grupo, idCampanha, cartItems, getAllocations, 'all');

    if (allocated > 0) return allocated;

    // Sem alocação neste combo: conta só os itens elegíveis que ainda não foram
    // consumidos por outro combo (grupo novo começa do 0, não soma kits de outros combos).
    const eligibleCartQty = getGroupCartQuantity(grupo, cartItems);
    const consumedByOthers = getGroupQuantityConsumedByOtherCombos(
        grupo,
        idCampanha,
        cartItems,
        getAllocations
    );

    return Math.max(eligibleCartQty - consumedByOthers, 0);
};

export const getGrupoStripProgress = (
    grupo: ComboRequirementGroup,
    idCampanha: string,
    cartItems: CartItems[],
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined
): Pick<ComboGroupProgress, 'required' | 'remaining'> & { fulfilled: number } => {
    const required = grupo.quantidade_base_por_kit ?? 0;
    const fulfilled = getGrupoFulfilledQuantity(grupo, idCampanha, cartItems, getAllocations);
    const remaining = Math.max(required - fulfilled, 0);

    return { required, fulfilled, remaining };
};

/** Status do selo para um grupo de exigência em uma instância específica do combo. */
export const getCartItemComboStripStatusForGrupo = (
    grupo: ComboRequirementGroup,
    idCampanha: string,
    comboInstanceId: string,
    lineAllocations: ComboCartItemAllocation[],
    cartItems: CartItems[],
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined
): CartItemComboStripStatus => {
    const grupoId = getGroupId(grupo);
    const grupoLabel = getGrupoLabel(grupo, grupoId || 'Grupo');
    const comboName = lineAllocations[0]?.nome_campanha || idCampanha;
    const required = grupo.quantidade_base_por_kit ?? 0;

    if (required <= 0) {
        return { isGroupComplete: false, remaining: 0, grupoLabel, comboName };
    }

    const currentQuantity = getGroupAllocatedQuantityForInstance(
        grupo,
        idCampanha,
        comboInstanceId,
        cartItems,
        getAllocations,
        'all'
    );
    const remaining = Math.max(required - currentQuantity, 0);

    return {
        isGroupComplete: required > 0 && remaining === 0 && currentQuantity > 0,
        remaining,
        grupoLabel,
        comboName,
    };
};

/** Status do selo por grupo de exigência do produto (verde = grupo cumprido, laranja = faltam embalagens no grupo). */
export const getCartItemComboStripStatusForLine = (
    lineAllocations: ComboCartItemAllocation[],
    combo: { id_campanha?: string; grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[],
    kitStatus: ComboKitStatus,
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined,
    lineContext?: CartLineComboStripContext,
    grupo?: ComboRequirementGroup
): CartItemComboStripStatus => {
    const resolvedGrupo =
        grupo ||
        findComboRequirementGroup(
            combo?.grupos_exigencia,
            (lineAllocations.find((entry) => entry.appliesDiscount !== false) || lineAllocations[0])?.grupoId
        );

    if (!resolvedGrupo || !combo?.id_campanha) {
        return { isGroupComplete: false, remaining: 0, grupoLabel: 'Grupo', comboName: '' };
    }

    const comboInstanceId =
        lineAllocations.find((entry) => entry.comboInstanceId)?.comboInstanceId ||
        `${combo.id_campanha}__KIT_1`;

    return getCartItemComboStripStatusForGrupo(
        resolvedGrupo,
        combo.id_campanha,
        comboInstanceId,
        lineAllocations,
        cartItems,
        getAllocations
    );
};

export const getGroupProgressForCartItemStrip = (
    allocation: ComboCartItemAllocation,
    combo: { grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[],
    kitStatus: ComboKitStatus
): ComboGroupProgress & { grupoLabel: string } => {
    const grupo = findComboRequirementGroup(combo?.grupos_exigencia, allocation.grupoId);
    const grupoLabel = getGrupoLabel(grupo, allocation.grupoId || 'Grupo');
    const required = grupo?.quantidade_base_por_kit ?? 0;

    if (!grupo || required <= 0) {
        return { required: 0, cartQuantity: 0, fulfilled: 0, remaining: 0, progressPercent: 0, grupoLabel };
    }

    if (kitStatus.activeCompleteKits > 0 && kitStatus.hasInProgressKit) {
        const nextKit = buildNextKitProgress(combo, cartItems, kitStatus.activeCompleteKits);
        const groupEntry = nextKit.groupProgress.find(
            (entry) => getGroupId(entry.grupo) === allocation.grupoId
        );

        if (groupEntry) {
            return { ...groupEntry, grupoLabel };
        }
    }

    return { ...buildGroupProgress(grupo, cartItems), grupoLabel };
};

export const getCartItemComboStripStatus = (
    allocation: ComboCartItemAllocation,
    combo: { id_campanha?: string; grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[],
    kitStatus: ComboKitStatus,
    getAllocations?: (uid: string) => ComboCartItemAllocation[] | undefined,
    lineContext?: CartLineComboStripContext
): CartItemComboStripStatus => {
    const resolveAllocations =
        getAllocations ??
        (() => undefined as ComboCartItemAllocation[] | undefined);

    const lineAllocations = allocation.id_campanha
        ? getLineAllocationsForCombo([allocation], allocation.id_campanha, {
              sku: allocation.cod_produto,
              option_uid: `${allocation.qtd_embalagem}`,
          })
        : [allocation];

    return getCartItemComboStripStatusForLine(
        lineAllocations.length ? lineAllocations : [allocation],
        combo,
        cartItems,
        kitStatus,
        resolveAllocations,
        lineContext
    );
};

export const buildComboProgress = (
    combo: { id_campanha?: string; grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[]
): ComboProgress => {
    const groups = combo?.grupos_exigencia || [];
    const groupProgress = groups.map((grupo) => ({
        grupo,
        ...buildGroupProgress(grupo, cartItems),
    }));

    const kitTotal = groups.reduce(
        (total, grupo) => total + (grupo.quantidade_base_por_kit ?? 0),
        0
    );
    const cartQuantity = groupProgress.reduce((total, group) => total + group.fulfilled, 0);
    const remaining = Math.max(kitTotal - cartQuantity, 0);
    const progressPercent = kitTotal > 0 ? Math.min((cartQuantity / kitTotal) * 100, 100) : 0;

    return {
        kitTotal,
        cartQuantity,
        fulfilled: cartQuantity,
        remaining,
        progressPercent,
        required: kitTotal,
        groupProgress,
    };
};

export const getComboMaxKitLimit = (combo: { regras_beneficio?: unknown }): number | null => {
    const regra = getComboBenefitRules(combo)[0];
    if (regra?.max_combo == null) return null;

    const limit = Number(regra.max_combo);
    return Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : null;
};

/** Quantos kits completos o carrinho comporta para este combo (sem considerar max_combo). */
export const countCompleteKitsInCart = (
    combo: { grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[]
): number => {
    const groups = combo?.grupos_exigencia || [];
    if (!groups.length) return 0;

    const kitsPerGroup = groups.map((grupo) => {
        const required = grupo.quantidade_base_por_kit ?? 0;
        if (required <= 0) return Number.POSITIVE_INFINITY;

        return Math.floor(getGroupCartQuantity(grupo, cartItems) / required);
    });

    const minKits = Math.min(...kitsPerGroup);
    return Number.isFinite(minKits) ? minKits : 0;
};

/** Kits completos que recebem benefício (respeitando max_combo; null = ilimitado). */
export const getActiveCompleteKitCount = (
    combo: { regras_beneficio?: unknown; grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[]
): number => {
    const completeKits = countCompleteKitsInCart(combo, cartItems);
    const maxLimit = getComboMaxKitLimit(combo);

    if (maxLimit == null) return completeKits;

    return Math.min(completeKits, maxLimit);
};

export const hasInProgressComboKit = (
    combo: { regras_beneficio?: unknown; grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[],
    activeCompleteKits: number
): boolean => {
    const groups = combo?.grupos_exigencia || [];
    if (!groups.length) return false;

    const hasEligibleProducts = groups.some((grupo) => getGroupCartQuantity(grupo, cartItems) > 0);
    if (!hasEligibleProducts) return false;

    const completeKits = countCompleteKitsInCart(combo, cartItems);
    const maxLimit = getComboMaxKitLimit(combo);

    if (maxLimit != null && completeKits > maxLimit) return false;

    if (activeCompleteKits <= 0) return true;

    const canBuildAnotherActiveKit = maxLimit == null || activeCompleteKits < maxLimit;
    if (!canBuildAnotherActiveKit) return false;

    return groups.some((grupo) => {
        const required = grupo.quantidade_base_por_kit ?? 0;
        const cartQty = getGroupCartQuantity(grupo, cartItems);
        return cartQty > activeCompleteKits * required;
    });
};

export const buildComboKitStatus = (
    combo: { regras_beneficio?: unknown; grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[]
): ComboKitStatus => {
    const completeKitsInCart = countCompleteKitsInCart(combo, cartItems);
    const maxComboLimit = getComboMaxKitLimit(combo);
    const activeCompleteKits = getActiveCompleteKitCount(combo, cartItems);
    const hasInProgressKit = hasInProgressComboKit(combo, cartItems, activeCompleteKits);

    return {
        completeKitsInCart,
        maxComboLimit,
        activeCompleteKits,
        hasInProgressKit,
    };
};

export const buildNextKitProgress = (
    combo: { grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[],
    activeCompleteKits: number
): ComboProgress => {
    const groups = combo?.grupos_exigencia || [];
    const groupProgress = groups.map((grupo) => {
        const required = grupo.quantidade_base_por_kit ?? 0;
        const cartQuantity = getGroupCartQuantity(grupo, cartItems);
        const towardNext = Math.max(cartQuantity - activeCompleteKits * required, 0);
        const fulfilled = Math.min(towardNext, required);
        const remaining = Math.max(required - towardNext, 0);
        const progressPercent = required > 0 ? Math.min((fulfilled / required) * 100, 100) : 0;

        return {
            grupo,
            required,
            cartQuantity: towardNext,
            fulfilled,
            remaining,
            progressPercent,
        };
    });

    const kitTotal = groups.reduce((total, grupo) => total + (grupo.quantidade_base_por_kit ?? 0), 0);
    const cartQuantity = groupProgress.reduce((total, group) => total + group.fulfilled, 0);
    const remaining = Math.max(kitTotal - cartQuantity, 0);
    const progressPercent = kitTotal > 0 ? Math.min((cartQuantity / kitTotal) * 100, 100) : 0;

    return {
        kitTotal,
        cartQuantity,
        fulfilled: cartQuantity,
        remaining,
        progressPercent,
        required: kitTotal,
        groupProgress,
    };
};

/** Ordem estável de inclusão no carrinho — usada no round-robin de alocação do grupo. */
const getCartItemsByInsertionOrder = (cartItems: CartItems[]) =>
    [...cartItems].sort((a, b) => {
        const da = a.addedAt ? new Date(a.addedAt).getTime() : 0;
        const db = b.addedAt ? new Date(b.addedAt).getTime() : 0;
        if (da !== db) return da - db;
        return String(a.uid).localeCompare(String(b.uid));
    });

const allocateGroupQuotaRoundRobin = (
    grupo: ComboRequirementGroup,
    orderedCartItems: CartItems[],
    remainingByUid: Map<string, number>,
    kitMultiplier = 1
): Map<string, number> => {
    const required = (grupo.quantidade_base_por_kit ?? 0) * kitMultiplier;
    const allocatedByUid = new Map<string, number>();

    if (required <= 0) return allocatedByUid;

    const eligibleItems = orderedCartItems.filter((item) =>
        (grupo.skus_elegiveis || []).some((sku) => isCartItemMatchingEligibleSku(item, sku))
    );

    eligibleItems.forEach((item) => allocatedByUid.set(item.uid, 0));

    let fulfilled = 0;

    // Distribui 1 embalagem por vez, em rodízio pela ordem de inclusão.
    // Assim, ao aumentar só uma linha, as demais mantêm a fatia já alocada no combo.
    while (fulfilled < required) {
        let progressed = false;

        for (const item of eligibleItems) {
            if (fulfilled >= required) break;

            const current = allocatedByUid.get(item.uid) ?? 0;
            const lineQuantity = getCartItemPackagingCount(item);
            const remainingForCombo = remainingByUid.get(item.uid) ?? 0;

            if (current >= lineQuantity || remainingForCombo <= 0) continue;

            allocatedByUid.set(item.uid, current + 1);
            remainingByUid.set(item.uid, remainingForCombo - 1);
            fulfilled += 1;
            progressed = true;
        }

        if (!progressed) break;
    }

    return allocatedByUid;
};

export const buildComboCartItemsMap = (cartItems: CartItems[], combos: any[]): ComboCartItemsMap =>
    buildComboInstanceAllocationsMap(cartItems, combos);

export const buildComboKitStatusFromInstances = (
    combo: { id_campanha: string; regras_beneficio?: unknown; grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[],
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined
): ComboKitStatus => {
    const instances = getComboInstanceStatuses(combo, cartItems, getAllocations);
    const completeDiscountable = instances.filter((instance) => instance.isComplete && instance.appliesDiscount);
    const completeInCart = instances.filter((instance) => instance.isComplete);

    return {
        completeKitsInCart: completeInCart.length,
        maxComboLimit: getComboMaxKitLimit(combo),
        activeCompleteKits: completeDiscountable.length,
        hasInProgressKit: instances.some((instance) => !instance.isComplete),
    };
};

/** Gera fatias de exibição por instância do combo para uma linha do carrinho. */
export type ComboDisplaySplitLine<T extends CartItems> = T & {
    comboPortion?: ComboCartDisplayPortion;
    displayQuantity: number;
    siblingQuantity: number;
    displayKey: string;
    cartQuantity: number;
    comboDiscountQuantity: number;
    comboInstanceId?: string;
    kitIndex?: number;
    allocationId?: string;
    grupoId?: string;
    idCampanha?: string;
    showLooseComboParticipant: boolean;
};

export const buildComboDisplaySplitsForCartLine = <T extends CartItems>(
    item: T,
    allocations: ComboCartItemAllocation[] | undefined
): ComboDisplaySplitLine<T>[] => {
    const relevant = (allocations || []).filter((entry) => entry.allocatedPackaging > 0);

    if (!relevant.length) {
        return [
            {
                ...item,
                displayQuantity: item.quantity,
                siblingQuantity: 0,
                displayKey: item.uid,
                cartQuantity: item.quantity,
                comboDiscountQuantity: 0,
                showLooseComboParticipant: false,
            },
        ];
    }

    const instanceIds = Array.from(
        new Set(relevant.map((entry) => entry.comboInstanceId).filter(Boolean) as string[])
    ).sort((a, b) => {
        const ka = parseComboInstanceId(a)?.kitIndex ?? 0;
        const kb = parseComboInstanceId(b)?.kitIndex ?? 0;
        return ka - kb;
    });

    const instanceSlices: ComboDisplaySplitLine<T>[] = instanceIds.flatMap((comboInstanceId) => {
        const instanceAllocs = relevant.filter((entry) => entry.comboInstanceId === comboInstanceId);
        const participatingQuantity = instanceAllocs.reduce(
            (sum, entry) => sum + entry.allocatedPackaging,
            0
        );
        const discountParticipatingQuantity = instanceAllocs
            .filter((entry) => entry.appliesDiscount !== false)
            .reduce((sum, entry) => sum + entry.allocatedPackaging, 0);

        if (participatingQuantity <= 0) return [];

        const kitIndex = instanceAllocs[0]?.kitIndex;
        const primaryAlloc = instanceAllocs[0];
        const siblingQuantity = getOtherInstancesAllocatedQuantity(relevant, comboInstanceId);

        return [
            {
                ...item,
                comboPortion: 'combo' as const,
                displayQuantity: participatingQuantity,
                siblingQuantity,
                displayKey: `${item.uid}-${comboInstanceId}`,
                cartQuantity: item.quantity,
                comboDiscountQuantity: discountParticipatingQuantity,
                comboInstanceId,
                kitIndex,
                allocationId: primaryAlloc?.allocationId,
                grupoId: primaryAlloc?.grupoId,
                idCampanha: primaryAlloc?.id_campanha,
                showLooseComboParticipant: false,
            },
        ];
    });

    const allocatedTotal = relevant.reduce((sum, entry) => sum + entry.allocatedPackaging, 0);
    const excessQty = Math.max(item.quantity - allocatedTotal, 0);

    if (excessQty > 0) {
        instanceSlices.push({
            ...item,
            comboPortion: 'excess',
            displayQuantity: excessQty,
            siblingQuantity: allocatedTotal,
            displayKey: `${item.uid}-excess`,
            cartQuantity: item.quantity,
            comboDiscountQuantity: 0,
            showLooseComboParticipant: true,
        });
    }

    return instanceSlices.length
        ? instanceSlices
        : [
              {
                  ...item,
                  displayQuantity: item.quantity,
                  siblingQuantity: 0,
                  displayKey: item.uid,
                  cartQuantity: item.quantity,
                  comboDiscountQuantity: 0,
                  showLooseComboParticipant: false,
              },
          ];
};

export const getActiveCombosInCart = (combos: any[], cartItems: CartItems[]): ActiveComboInCart[] => {
    return combos
        .map((combo) => ({
            combo: {
                ...combo,
                nome_campanha: normalizeComboName(combo),
            },
            progress: buildComboProgress(combo, cartItems),
            activeCompleteKits: getActiveCompleteKitCount(combo, cartItems),
        }))
        .filter(({ activeCompleteKits }) => activeCompleteKits > 0);
};

export const isComboComplete = (
    combo: { regras_beneficio?: unknown; grupos_exigencia?: ComboRequirementGroup[] },
    cartItems: CartItems[]
) => {
    return getActiveCompleteKitCount(combo, cartItems) > 0;
};

export interface ComboBenefitRule {
    tipo_desconto?: string;
    valor_percentual?: number | null;
    tabela_precos_especiais?: unknown;
    max_combo?: number | null;
}

export type ComboPriceBenefit =
    | { type: 'PERCENTUAL'; percent: number }
    | { type: 'PRECO_FIXO'; fixedPrice: number };

export interface ComboFixedPriceEntry {
    cod_produto?: number | string;
    qtd_embalagem?: number | string;
    vlr_preco?: number;
    preco?: number;
    valor?: number;
    VLR_PRECO?: number;
}

const readFixedPriceValue = (value: unknown): number | null => {
    if (value == null) return null;

    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
        const row = value as ComboFixedPriceEntry;
        const candidate = row.vlr_preco ?? row.preco ?? row.valor ?? row.VLR_PRECO;
        if (candidate == null) return null;

        const parsed = Number(candidate);
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};

const matchesComboPriceRow = (row: Record<string, unknown>, option_uid: string) => {
    const [packing, quantity] = option_uid?.split('_') || [];
    const qtdMultiplo = Number(row.QTD_MULTIPLO_VENDA || 0);

    if (packing === 'PK' || qtdMultiplo > 1) {
        return (
            String(row.SG_EMBALAGEM || '') === packing &&
            String(row.QTD_MULTIPLO_VENDA ?? '') === quantity
        );
    }

    return (
        String(row.SG_EMBALAGEM || 'UN') === (packing || 'UN') &&
        String(row.QTD_EMBALAGEM ?? '') === quantity
    );
};

export const getComboFixedPriceFromTable = (
    tabela: unknown,
    codProduto: number,
    qtdEmbalagem?: number
): number | null => {
    if (tabela == null) return null;

    if (Array.isArray(tabela)) {
        const exactMatch = tabela.find((entry) => {
            const row = entry as ComboFixedPriceEntry;
            if (Number(row.cod_produto) !== Number(codProduto)) return false;

            if (qtdEmbalagem == null || row.qtd_embalagem == null) return true;

            return Number(row.qtd_embalagem) === Number(qtdEmbalagem);
        }) as ComboFixedPriceEntry | undefined;

        if (exactMatch) {
            const price = readFixedPriceValue(exactMatch);
            if (price != null) return price;
        }

        const productOnlyMatch = tabela.find(
            (entry) => Number((entry as ComboFixedPriceEntry).cod_produto) === Number(codProduto)
        ) as ComboFixedPriceEntry | undefined;

        return readFixedPriceValue(productOnlyMatch);
    }

    if (typeof tabela !== 'object') return null;

    const map = tabela as Record<string, unknown>;
    const productKeys = [String(codProduto), `${codProduto}`];
    let productEntry: unknown;

    for (const key of productKeys) {
        if (map[key] != null) {
            productEntry = map[key];
            break;
        }
    }

    if (productEntry == null) {
        const compositeKey = qtdEmbalagem != null ? `${codProduto}_${qtdEmbalagem}` : null;
        if (compositeKey && map[compositeKey] != null) {
            return readFixedPriceValue(map[compositeKey]);
        }
        return null;
    }

    if (typeof productEntry === 'object' && !Array.isArray(productEntry) && qtdEmbalagem != null) {
        const packagingMap = productEntry as Record<string, unknown>;
        const packagingEntry =
            packagingMap[String(qtdEmbalagem)] ?? packagingMap[`${qtdEmbalagem}`];

        const packagingPrice = readFixedPriceValue(packagingEntry);
        if (packagingPrice != null) return packagingPrice;
    }

    return readFixedPriceValue(productEntry);
};

export const getComboBenefitRules = (combo: { regras_beneficio?: unknown }): ComboBenefitRule[] => {
    const raw = combo?.regras_beneficio;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as ComboBenefitRule[];
    return [raw as ComboBenefitRule];
};

export const getComboPercentFromRule = (regra?: ComboBenefitRule | null): number | null => {
    if (!regra) return null;

    const tipo = String(regra.tipo_desconto || '').toUpperCase();
    if (tipo !== 'PERCENTUAL' || regra.valor_percentual == null) return null;

    const percent = Number(regra.valor_percentual);
    return Number.isFinite(percent) && percent > 0 ? percent : null;
};

export const applyComboPercentDiscountToProduct = (
    product: ProductProps,
    percent: number
): ProductProps => {
    if (!percent || percent <= 0) return product;

    const rawPrecos = product?.PRECO || {};
    const precoList = normalizePrecoListForCalc(rawPrecos);
    if (!precoList.length) return product;

    const discountedPrecos = precoList.map((row) => ({
        ...row,
        VLR_PRECO: HandlePrices.calculaPercentual(Number(row.VLR_PRECO || 0), -percent),
    }));

    return {
        ...product,
        PRECO: discountedPrecos as unknown as ProductProps['PRECO'],
    };
};

export const applyComboFixedPriceToProduct = (
    product: ProductProps,
    fixedPrice: number,
    option_uid: string
): ProductProps => {
    if (!Number.isFinite(fixedPrice) || fixedPrice <= 0) return product;

    const rawPrecos = product?.PRECO || {};
    const precoList = normalizePrecoListForCalc(rawPrecos);
    if (!precoList.length) return product;

    const hasPackagingMatch = precoList.some((row) => matchesComboPriceRow(row, option_uid));

    const discountedPrecos = precoList.map((row) => {
        if (hasPackagingMatch && !matchesComboPriceRow(row, option_uid)) {
            return row;
        }

        return {
            ...row,
            VLR_PRECO: fixedPrice,
        };
    });

    return {
        ...product,
        PRECO: discountedPrecos as unknown as ProductProps['PRECO'],
    };
};

export const applyComboPriceBenefitToProduct = (
    product: ProductProps,
    benefit: ComboPriceBenefit,
    option_uid: string
): ProductProps => {
    if (benefit.type === 'PERCENTUAL') {
        return applyComboPercentDiscountToProduct(product, benefit.percent);
    }

    return applyComboFixedPriceToProduct(product, benefit.fixedPrice, option_uid);
};

export const getComboPriceBenefitForCartItem = (
    allocations: ComboCartItemAllocation[] | undefined,
    combos: Array<{ id_campanha?: string; regras_beneficio?: unknown }>,
    isComboCompleteById: (idCampanha: string) => boolean,
    item: Pick<CartItems, 'sku' | 'option_uid'>
): ComboPriceBenefit | null => {
    if (!allocations?.length) return null;

    const qtdEmbalagem = getCartItemPackagingQty(item);
    const lineAllocations = allocations.filter(
        (entry) =>
            Number(entry.cod_produto) === Number(item.sku) &&
            Number(entry.qtd_embalagem) === qtdEmbalagem &&
            entry.allocatedPackaging > 0 &&
            entry.appliesDiscount !== false
    );

    if (!lineAllocations.length) return null;

    for (const allocation of lineAllocations) {
        if (!isComboCompleteById(allocation.id_campanha)) continue;

        const combo = combos.find((item) => item.id_campanha === allocation.id_campanha);
        if (!combo) continue;

        const regra = getComboBenefitRules(combo)[0];
        const tipo = String(regra?.tipo_desconto || '').toUpperCase();

        if (tipo === 'PERCENTUAL') {
            const percent = getComboPercentFromRule(regra);
            if (percent != null) return { type: 'PERCENTUAL', percent };
        }

        if (tipo === 'PRECO_FIXO') {
            const fixedPrice = getComboFixedPriceFromTable(
                regra?.tabela_precos_especiais,
                Number(item.sku),
                Number.isFinite(qtdEmbalagem) ? qtdEmbalagem : undefined
            );

            if (fixedPrice != null) return { type: 'PRECO_FIXO', fixedPrice };
        }
    }

    return null;
};

export const getActiveComboPromotionTypeForCartItem = (
    item: Pick<CartItems, 'sku' | 'option_uid'>,
    allocations: ComboCartItemAllocation[] | undefined,
    isComboCompleteById: (idCampanha: string) => boolean
): string | null => {
    if (!allocations?.length) return null;

    const qtdEmbalagem = getCartItemPackagingQty(item);
    const lineAllocations = allocations.filter(
        (entry) =>
            Number(entry.cod_produto) === Number(item.sku) &&
            Number(entry.qtd_embalagem) === qtdEmbalagem &&
            entry.allocatedPackaging > 0 &&
            entry.appliesDiscount !== false
    );

    for (const allocation of lineAllocations) {
        if (!isComboCompleteById(allocation.id_campanha)) continue;
        return allocation.id_campanha;
    }

    return null;
};

export const getComboPercentDiscountForCartItem = (
    allocations: ComboCartItemAllocation[] | undefined,
    combos: Array<{ id_campanha?: string; regras_beneficio?: unknown }>,
    isComboCompleteById: (idCampanha: string) => boolean
): number | null => {
    if (!allocations?.length) return null;

    for (const allocation of allocations) {
        if (!isComboCompleteById(allocation.id_campanha)) continue;

        const combo = combos.find((item) => item.id_campanha === allocation.id_campanha);
        if (!combo) continue;

        const regra = getComboBenefitRules(combo)[0];
        const percent = getComboPercentFromRule(regra);

        if (percent != null) return percent;
    }

    return null;
};
